
import { defineComponent, reactive, ref } from 'vue'
import { CloudLibraryUpdateService, STATE, TASK_STATUS, ERROR_MESSAGE } from './cloud-library-update.service'
import { useRoute } from 'vue-router'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { BrandConfig, useBrandCustomStore } from '@/services/brand-custom.service'

const FILE_MAX_SIZE = 100 // M

export default defineComponent({
  setup () {
    const injector = useReflectiveInjector([CloudLibraryUpdateService])
    const cloudLibraryUpdateService = injector.get(CloudLibraryUpdateService)

    const route = useRoute()
    const uploadNode = ref()
    let statusTimer: any = null

    const viewModel = reactive({
      token: route.query.token as string || '',
      permitToken: false,
      state: 0,
      taskStatusMessage: '',
      uploadFile: null,
      fileName: '',
      percentage: 0,
      updateLibraryKey: '',
      libraryFilePath: '',
      errorMsg: '',
    })

    function checkToken () {
      cloudLibraryUpdateService.checkToken({ token: viewModel.token }).then(() => {
        viewModel.permitToken = true
      }).catch(error => {
        let message = '非法授权访问'
        if (error.responseBody && error.responseBody.ret) {
          message = ERROR_MESSAGE[error.responseBody.ret] || error.responseBody.msg
        }

        viewModel.state = STATE.FAIL
        viewModel.errorMsg = message
      })
    }

    function verifyFile (file: File): boolean {
      // eslint-disable-next-line no-magic-numbers
      return file.size < FILE_MAX_SIZE * 1024 * 1024
    }

    function getState () {
      cloudLibraryUpdateService.getLibraryStatus({
        key: viewModel.updateLibraryKey
      }).then(response => {
        switch (response.status) {
          case TASK_STATUS.UPLOAD_SUCCESS:
            viewModel.taskStatusMessage = '授权文件上传成功，正在解析'
            break
          case TASK_STATUS.GET_SAMPLE_SUCCESS:
            viewModel.taskStatusMessage = '解析授权成功，正在生成离线库'
            break
          case TASK_STATUS.BUILD_SUCCESS:
            viewModel.libraryFilePath = response.file_path
            viewModel.state = STATE.SUCCESS
            clearTimeout(statusTimer)
            return
          case TASK_STATUS.BUILD_FAIL:
            viewModel.state = STATE.FAIL
            viewModel.errorMsg = '云特征库同步失败'
            clearTimeout(statusTimer)
            return
          default:
        }
        if (!response.file_path) {
          clearTimeout(statusTimer)
          statusTimer = setTimeout(() => {
            getState()
            // eslint-disable-next-line no-magic-numbers
          }, 4000)
        }
      })
    }

    checkToken()

    return {
      STATE,
      brandConfig: useBrandCustomStore().brandConfig as BrandConfig,
      uploadNode,
      viewModel,
      handleChange (file: any) {
        if (file?.status === 'ready') {
          viewModel.uploadFile = file
          viewModel.fileName = file.name
          viewModel.percentage = 0
        }
      },
      async handleHttpUpload () {
        if (!verifyFile(viewModel.uploadFile?.raw)) {
          viewModel.state = STATE.FAIL
          viewModel.errorMsg = `文件大小不能超过 ${FILE_MAX_SIZE}M`
          return
        }

        viewModel.state = STATE.UPLOADING

        try {
          const checkUploadResponse = await cloudLibraryUpdateService.checkUpload({ token: viewModel.token })

          const { host, access_id, directory, policy, signature } = checkUploadResponse
          const filename = cloudLibraryUpdateService.generateUUID() + '.key'
          const data = new FormData()
          data.append('key', directory + filename)
          data.append('success_action_status', '200')
          data.append('OSSAccessKeyId', access_id)
          data.append('policy', policy)
          data.append('signature', signature)
          data.append('file', viewModel.uploadFile?.raw)

          await cloudLibraryUpdateService.licenceFileUpload(
            host,
            data,
            (progress: any) => {
              // eslint-disable-next-line no-magic-numbers
              viewModel.percentage = Math.floor((progress.loaded / progress.total) * 100)
              // eslint-disable-next-line no-magic-numbers
              if (viewModel.percentage === 100) {
                viewModel.state = STATE.UPDATING
                viewModel.taskStatusMessage = '授权文件上传成功，正在解析'
              }
            }
          )

          const saveResponse = await cloudLibraryUpdateService.uploadSave({ token: viewModel.token, file_name: filename })

          viewModel.updateLibraryKey = saveResponse.key
          getState()
        } catch (error: any) {
          let message = '云特征库同步失败'
          if (error.responseBody && error.responseBody.ret) {
            message = ERROR_MESSAGE[error.responseBody.ret] || error.responseBody.msg
          }

          viewModel.state = STATE.FAIL
          viewModel.errorMsg = message
        }
      },
    }
  }
})
